import { useMemo, useReducer, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import {
  PlanHuntWizardShell,
  PlanHuntDrivesStep,
  emptyDriveNames,
  planHuntStepState,
  planHuntInvitationFilled,
  type PlanHuntStepFilled,
  ConfirmModal,
  defaultStartTime,
  useToast,
  type PlanHuntWizardStep,
  type PlanHuntDrive,
  type PlanHuntMember,
  // The wizard has no appearance panel; its map takes the layer switches saved on
  // the team map, so a layer hidden there stays hidden while planning.
  useHuntMapAppearance,
  buildPinTypeCatalog,
  catalogLocale,
} from '@wehunt/ui'
import { useAuth } from '@wehunt/core-web'
import { useMyGroups } from '../../teams/useMyGroups'
import { useGroupMembers } from '../../teams/useGroupMembers'
import { useGroupDocuments } from '../../teams/useGroupDocuments'
import { useDraftHunt, type HuntableGameSelectionDetail } from '../useDraftHunt'
import { useCreateDraftHunt, useUpdateDraftHunt, useDeleteDraftHunt, useFinalizeDraftHunt } from '../useDraftHuntMutations'
import { BasicsStep, hasValidSchedule, type BasicsState } from './BasicsStep'
import { LeadersStep } from './LeadersStep'
import { ParticipantsStep } from './ParticipantsStep'
import { InvitationStep } from './InvitationStep'
import { GameStep } from './GameStep'
import { useUploadHuntImage } from './useUploadHuntImage'
import { useGameNamesCatalog } from './useGameNamesCatalog'
import { useUploadHuntAttachment, type HuntAttachmentFile } from './useUploadHuntAttachment'
import { useResolveHuntAttachmentUrl } from './useResolveHuntAttachmentUrl'
import type { AttachmentFileDetail } from '../useDraftHunt'
import { wizardDrivesToDetail, detailDrivesToWizard } from './planDriveConverters'
import { ApiError } from '../../../shared/api/ApiError'
import { useApiFetch } from '../../../shared/api'
import { parseInviteBatch, type MemberInvite } from '../../teams/inviteInput'
import { postHuntGuest } from '../useInviteHuntGuest'
import { huntGuestInviteHuntState, trackHuntGuestInvited } from '../huntGuestInviteTracking'

/**
 * The human-readable validation message the API returned, if any. A failed
 * save/finalize comes back as a 422 whose body is `{ error, code }`; apiFetch
 * parses that into ApiError.problem, so the useful text lives at
 * `problem.error` (ApiError.message is only ever "HTTP 422"). Surfacing it is
 * what turns a silent "nothing happened" into "At least one huntable game
 * species is required." Returns null for network/unknown errors so the caller
 * can fall back to a localized generic message.
 */
/**
 * The hunt name to persist. The name is optional (matching mobile): when the
 * user leaves it blank we fall back to the team name, which is exactly what
 * mobile shows for an unnamed hunt (its displayName chain ends at the group
 * name). Defaulting here — rather than sending an empty name — also keeps the
 * .NET name validation satisfied without a server change. Null only if there is
 * no team yet (the finalize gate blocks sending in that case anyway).
 */
function nameOrTeamDefault(huntName: string, teamName: string | undefined): string | null {
  const trimmed = huntName.trim()
  if (trimmed.length > 0) return trimmed
  return teamName && teamName.trim().length > 0 ? teamName : null
}

function serverValidationMessage(err: unknown): string | null {
  if (err instanceof ApiError) {
    const detail = (err.problem as { error?: unknown }).error
    if (typeof detail === 'string' && detail.trim().length > 0) return detail
  }
  return null
}
import { useGroupMapPins } from '../../teams/useGroupMapPins'
import { useGroupHuntingGrounds } from '../../teams/useGroupHuntingGrounds'
import { usePinTypeCatalogEntries } from '../../teams/pinTypeCatalog/usePinTypeCatalogEntries'
import { resolveDriveBaseAreas } from './huntAreaSelection'

interface WizardState {
  basics: BasicsState
  leaderIds: string[]
  participantIds: string[]
  invitationMessage: string | null
  attachmentDocumentIds: string[]
  attachmentFiles: HuntAttachmentFile[]
  drives: PlanHuntDrive[]
  huntableGames: HuntableGameSelectionDetail[]
  /**
   * Whether hunters see only the planned drives on the hunt map (true) or the
   * whole hunting ground (false, the default). Set by the "What hunters see on
   * the map" toggle on the Drives step (estate hunts only); seeded from a loaded
   * draft and round-tripped on every save. The .NET API derives the enforced
   * per-area/per-pin filter from the drives themselves, so this flag is all the
   * web sends for map scope.
   */
  restrictMapToPlannedDrives: boolean
  /**
   * Guests to invite by email or phone, collected in the participants step and
   * sent once finalize creates the hunt and its join code (the invite API needs
   * both). Client-only wizard state — not written to the draft document — so a
   * full page reload before finalize drops them; a client-side step change keeps
   * them. Mobile sends each immediately because its plan already exists
   * server-side; web's draft has no hunt id or join code until finalize, so web
   * collects here and sends there.
   */
  draftGuests: MemberInvite[]
  draftId: string | null
  currentStepIndex: number
  lastStepIndex: number
}

export const initialState: WizardState = {
  basics: { groupId: null, huntName: '', startDate: '', startTime: '', endDate: '', endTime: '' },
  leaderIds: [],
  participantIds: [],
  invitationMessage: null,
  attachmentDocumentIds: [],
  attachmentFiles: [],
  drives: [],
  huntableGames: [],
  restrictMapToPlannedDrives: false,
  draftGuests: [],
  draftId: null,
  currentStepIndex: 0,
  lastStepIndex: 0,
}

/**
 * Index of the last wizard step (Game) — the wizard has six steps, 0..5.
 *
 * Drafts persist the step the hunter reached, and the wizard used to have seven
 * steps (an Areas step sat between Invitation and Drives), so a draft saved
 * before that step was removed can carry an index past the end. Every index
 * arriving from a draft is clamped to this, otherwise `steps[index]` is
 * undefined, the current-step lookup falls back to Basics, and the wizard
 * renders Basics while the rail marks all six steps done.
 */
export const LAST_STEP_INDEX = 5

/** Clamp a persisted step index into the range the wizard actually has. */
function clampStepIndex(index: number): number {
  return Math.min(LAST_STEP_INDEX, Math.max(0, index))
}

type Action =
  | { type: 'set-basics'; value: BasicsState }
  | { type: 'set-group'; value: string }
  | { type: 'set-leaders'; value: string[] }
  | { type: 'set-participants'; value: string[] }
  | { type: 'set-invitation-message'; value: string | null }
  | { type: 'set-attachments'; value: string[] }
  | { type: 'set-attachment-files'; value: HuntAttachmentFile[] }
  | { type: 'set-drives'; value: PlanHuntDrive[] }
  | { type: 'set-huntable-games'; value: HuntableGameSelectionDetail[] }
  | { type: 'set-restrict-map'; value: boolean }
  | { type: 'add-guest'; value: MemberInvite }
  | { type: 'remove-guest'; value: number }
  | { type: 'set-step'; value: number }
  | { type: 'load-draft'; value: { id: string; basics: BasicsState; leaderIds: string[]; participantIds: string[]; invitationMessage: string | null; attachmentDocumentIds: string[]; attachmentFiles: HuntAttachmentFile[]; drives: PlanHuntDrive[]; huntableGames: HuntableGameSelectionDetail[]; restrictMapToPlannedDrives: boolean; draftGuests: MemberInvite[]; lastStepIndex: number } }
  | { type: 'set-draft-id'; value: string }

export function reducer(s: WizardState, a: Action): WizardState {
  switch (a.type) {
    // Re-associate the draft when the team changes: the current draft lives
    // under the previously-selected group, so keeping its id would make every
    // subsequent GET/PUT hit /groups/{newTeam}/hunts/drafts/{oldDraft} → 404.
    // Dropping it makes the next Continue create a fresh draft under the new team.
    case 'set-basics': {
      const teamChanged = a.value.groupId !== s.basics.groupId
      return {
        ...s,
        basics: a.value,
        draftId: teamChanged ? null : s.draftId,
      }
    }
    // Set only the team, merging into the CURRENT basics (not a captured
    // snapshot). The sole-team pre-select effect fires from a render where
    // basics may since have been hydrated from a draft; a full set-basics with
    // a stale closure would wipe the loaded start date. Same draft re-association
    // rule as set-basics: a real team change drops the draft id.
    case 'set-group': {
      // Same rule as set-basics: a real team change drops the draft id.
      const teamChanged = a.value !== s.basics.groupId
      return {
        ...s,
        basics: { ...s.basics, groupId: a.value },
        draftId: teamChanged ? null : s.draftId,
      }
    }
    case 'set-leaders':               return { ...s, leaderIds: a.value }
    case 'set-participants':          return { ...s, participantIds: a.value }
    case 'set-invitation-message':    return { ...s, invitationMessage: a.value }
    case 'set-attachments':           return { ...s, attachmentDocumentIds: a.value }
    case 'set-attachment-files':      return { ...s, attachmentFiles: a.value }
    case 'set-drives':                return { ...s, drives: a.value }
    case 'set-huntable-games':        return { ...s, huntableGames: a.value }
    case 'set-restrict-map':          return { ...s, restrictMapToPlannedDrives: a.value }
    case 'add-guest':                 return { ...s, draftGuests: [...s.draftGuests, a.value] }
    case 'remove-guest':              return { ...s, draftGuests: s.draftGuests.filter((_, i) => i !== a.value) }
    case 'set-step':                  return { ...s, currentStepIndex: a.value, lastStepIndex: Math.max(s.lastStepIndex, a.value) }
    case 'load-draft':                return { ...s, draftId: a.value.id, basics: a.value.basics, leaderIds: a.value.leaderIds, participantIds: a.value.participantIds, invitationMessage: a.value.invitationMessage, attachmentDocumentIds: a.value.attachmentDocumentIds, attachmentFiles: a.value.attachmentFiles, drives: a.value.drives, huntableGames: a.value.huntableGames, restrictMapToPlannedDrives: a.value.restrictMapToPlannedDrives, draftGuests: a.value.draftGuests, lastStepIndex: clampStepIndex(a.value.lastStepIndex), currentStepIndex: clampStepIndex(a.value.lastStepIndex) }
    case 'set-draft-id':              return { ...s, draftId: a.value }
  }
}

function detailsToWizardFiles(files: AttachmentFileDetail[] | undefined): HuntAttachmentFile[] {
  return (files ?? []).map((f) => ({
    fileName: f.fileName,
    storagePath: f.storagePath,
    mimeType: f.mimeType,
    sizeBytes: f.sizeBytes,
    // Round-tripped deliberately. Editing a draft re-sends the whole attachment
    // list, so dropping the URL here would blank the mobile mirror for files the
    // user never touched — the original bug, reintroduced on every later edit.
    downloadUrl: f.downloadUrl ?? null,
  }))
}

export function basicsToCommandTimes(b: BasicsState): { startTime: string | null; endTime: string | null } {
  if (!b.startDate || !b.startTime) return { startTime: null, endTime: null }
  return {
    startTime: new Date(`${b.startDate}T${b.startTime}:00`).toISOString(),
    // End is optional — an open-ended hunt sends a null end time.
    endTime: b.endDate && b.endTime ? new Date(`${b.endDate}T${b.endTime}:00`).toISOString() : null,
  }
}

export interface PlanHuntPageProps {
  /**
   * When on, a drive can be based on a single sub-area rather than the whole
   * ground, which narrows the stand pins the drive editor offers to those
   * inside it.
   *
   * Off by default. The drives step then receives no areas at all, so the
   * editor's area picker collapses to "Entire hunting ground" on its own and
   * the step behaves exactly as it did before this flag existed — the reason
   * the gate is the data passed in rather than a branch inside the shared
   * component.
   */
  subAreaScopeEnabled?: boolean
}

export function PlanHuntPage({ subAreaScopeEnabled = false }: PlanHuntPageProps = {}) {
  const huntMapAppearance = useHuntMapAppearance()
  const { t, i18n } = useTranslation('planHunt')
  const navigate = useNavigate()
  const { showToast } = useToast()
  const search = useRouterState({ select: (s) => s.location.search }) as { draftId?: string; groupId?: string; date?: string }
  const { state: authState } = useAuth()
  const currentUserId = authState.status === 'signed-in' ? authState.user.uid : ''

  const [state, dispatch] = useReducer(reducer, initialState)
  const { data: myGroupsData } = useMyGroups()
  const teams = useMemo(
    () => (myGroupsData?.groups ?? []).map((g) => ({ id: g.id, name: g.name, members: g.memberCount, isEstate: g.isEstate })),
    [myGroupsData],
  )

  // Seed from URL ?draftId (+ ?groupId). The draft read is keyed by group, but on
  // reopen the group isn't in wizard state yet — it comes FROM the draft — so the
  // URL carries it. Prefer the URL group: it's stable, so changing the team in the
  // Basics step after the draft loads can't re-key this read onto a group the draft
  // doesn't live under (a spurious 404). Fall back to wizard state for links that
  // predate the ?groupId param.
  const draftId = search?.draftId ?? null
  const draftGroupId = search?.groupId || state.basics.groupId || null
  const { data: draftDetail } = useDraftHunt(draftGroupId, draftId)
  useEffect(() => {
    if (draftDetail && state.draftId !== draftDetail.id) {
      const startDate = draftDetail.startTime ? new Date(draftDetail.startTime) : null
      const endDate = draftDetail.endTime ? new Date(draftDetail.endTime) : null
      dispatch({
        type: 'load-draft',
        value: {
          id: draftDetail.id,
          basics: {
            groupId: draftDetail.groupId,
            huntName: draftDetail.huntName ?? '',
            startDate: startDate ? startDate.toISOString().slice(0, 10) : '',
            startTime: startDate ? startDate.toISOString().slice(11, 16) : '',
            endDate: endDate ? endDate.toISOString().slice(0, 10) : '',
            endTime: endDate ? endDate.toISOString().slice(11, 16) : '',
          },
          leaderIds: draftDetail.leaderIds,
          participantIds: draftDetail.participantIds,
          invitationMessage: draftDetail.invitationMessage,
          attachmentDocumentIds: draftDetail.attachmentDocumentIds,
          attachmentFiles: detailsToWizardFiles(draftDetail.attachmentFiles),
          drives: detailDrivesToWizard(draftDetail.drives ?? []),
          huntableGames: draftDetail.huntableGames ?? [],
          restrictMapToPlannedDrives: draftDetail.restrictMapToPlannedDrives,
          // Rehydrate the guests the API persisted on the plan so the
          // participants step shows them again (the fix for guests vanishing on
          // reopen). Absent on drafts saved before the field existed.
          draftGuests: draftDetail.guests ?? [],
          lastStepIndex: draftDetail.lastStepIndex,
        },
      })
    }
  }, [draftDetail, state.draftId])

  // Pre-fill the start date when planning was launched for a specific calendar
  // day (Plan hunt from the calendar panel). Runs once for a fresh plan (no
  // draft, no start date yet); the start time uses the CLUB-150 smart default.
  const prefillDate = search?.date
  useEffect(() => {
    if (prefillDate && !draftId && !state.draftId && !state.basics.startDate) {
      dispatch({
        type: 'set-basics',
        value: { ...state.basics, startDate: prefillDate, startTime: defaultStartTime(prefillDate, new Date()) },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillDate, draftId, state.draftId])

  // Pre-select sole team
  useEffect(() => {
    if (state.basics.groupId === null && teams.length === 1) {
      dispatch({ type: 'set-group', value: teams[0].id })
    }
  }, [teams, state.basics.groupId])

  const { data: members } = useGroupMembers(state.basics.groupId ?? '')

  // Pre-check current user on step 2 entry
  useEffect(() => {
    if (state.currentStepIndex === 1 && state.leaderIds.length === 0 && currentUserId) {
      dispatch({ type: 'set-leaders', value: [currentUserId] })
    }
  }, [state.currentStepIndex, state.leaderIds.length, currentUserId])

  // Preselect ALL team members as participants on first entering Step 3.
  // (Leaders are members too, so this also covers the leader-pre-check.) Runs
  // once — the empty-participants guard means clearing the list afterwards
  // doesn't re-seed and fight the user.
  const rosterIds = useMemo(
    () => (members?.members ?? []).filter((m) => !m.deleted).map((m) => m.id),
    [members],
  )
  useEffect(() => {
    if (
      state.lastStepIndex < 3 &&
      state.currentStepIndex === 2 &&
      state.participantIds.length === 0 &&
      rosterIds.length > 0
    ) {
      dispatch({ type: 'set-participants', value: rosterIds })
    }
  }, [state.currentStepIndex, state.lastStepIndex, state.participantIds.length, rosterIds])

  // Leaders + participants sent on save/finalize. Their preselects only fire on
  // their own steps — leaders ([currentUserId]) on step 1, participants (all
  // members) on step 2 as the roster loads. A user who jumps straight to a later
  // step via the rail, or whose roster resolves after they passed those steps,
  // reaches Save & send with both still empty, which 422s the draft save
  // (lastStepIndex >= 3) and the server finalize ("At least one leader/participant
  // is required."). Both have documented defaults — the current user leads, all
  // members participate — so fall back to them when nothing was explicitly
  // chosen. An empty selection is never a valid send, so this only ever restores
  // the default, never overrides a real choice. Participants are forced to
  // include the leaders because the backend requires participantIds ⊇ leaderIds.
  const effectiveLeaderIds =
    state.leaderIds.length > 0 ? state.leaderIds : currentUserId ? [currentUserId] : []
  const baseParticipantIds = state.participantIds.length > 0 ? state.participantIds : rosterIds
  const effectiveParticipantIds = Array.from(new Set([...baseParticipantIds, ...effectiveLeaderIds]))

  const { docTree } = useGroupDocuments(state.basics.groupId ?? '')
  const docList = useMemo(() => Object.values(docTree ?? {}), [docTree])
  const create = useCreateDraftHunt()
  const update = useUpdateDraftHunt()
  const del = useDeleteDraftHunt()
  const finalize = useFinalizeDraftHunt()
  const uploadImage = useUploadHuntImage(state.draftId)
  const uploadAttachment = useUploadHuntAttachment(state.draftId)
  const resolveAttachmentUrl = useResolveHuntAttachmentUrl()
  // Used to POST each collected guest to the hunt-guest endpoint once finalize
  // has created the hunt (see handleFinalize). The hunt id isn't known until
  // then, so this can't go through useInviteHuntGuest, which binds the id up front.
  const call = useApiFetch()
  // Remote-Config-driven game catalog. Falls back to the bundled
  // HUNTABLE_GAME_CATALOG inside the hook if RC is unavailable, so the
  // wizard never renders an empty species list.
  const { catalog: gameCatalog } = useGameNamesCatalog()
  const teamPins = useGroupMapPins(state.basics.groupId ?? '')
  const grounds = useGroupHuntingGrounds(state.basics.groupId ?? '')
  // The whole ground, not its first parcel: reading [0] drew one parcel and
  // called it the boundary, on a screen where a hunter is placing stands.
  // Areas a drive may be based on — sub-areas when the team has them, otherwise
  // the hunt-area borders. Empty while the flag is off, which is the entire
  // gate. See resolveDriveBaseAreas for why each branch exists.
  const driveBaseSubAreas = useMemo(
    () =>
      resolveDriveBaseAreas(subAreaScopeEnabled, {
        boundaryAreas: grounds.boundaryAreas,
        subAreaPolygons: grounds.subAreaPolygons,
      }),
    [subAreaScopeEnabled, grounds.boundaryAreas, grounds.subAreaPolygons],
  )
  // Pin-type catalog (the Remote Config `mapPinTypes2`, same source mobile uses)
  // to label the per-drive map-pin groups by their localized type name. Built
  // here and threaded into the drives step — the shared editor stays
  // presentation-only and never fetches.
  const { entries: pinTypeEntries, ready: pinTypeReady } = usePinTypeCatalogEntries()
  const pinTypeCatalog = useMemo(
    () => buildPinTypeCatalog(pinTypeEntries, catalogLocale(i18n.language), pinTypeReady),
    [pinTypeEntries, i18n.language, pinTypeReady],
  )

  const driveParticipants: PlanHuntMember[] = (members?.members ?? [])
    .filter((m) => !m.deleted && state.participantIds.includes(m.id))
    .map((m) => ({ id: m.id, name: m.name, avatar: m.imageUrl ?? (m.avatar?.startsWith('http') ? m.avatar : null) }))
  const selectedTeam = teams.find((t) => t.id === state.basics.groupId)
  const teamName = selectedTeam?.name
  // The "What hunters see on the map" toggle and the per-drive sub-area/pin
  // scope share one gate: the driveSubAreaScope flag (CLUB-1076 — this was
  // estate-only, now it is offered to every team the flag reaches). The .NET API
  // ignores the old hand-picked filter and derives it from the drives, so the
  // wizard sends only the restrict flag for map scope.

  async function handleContinue() {
    // Persist once the schedule is valid. The hunt name is optional now — a
    // blank name is defaulted to the team name in the body (see
    // nameOrTeamDefault), so the backend never sees a null name and there is no
    // name to gate on. A failed save is logged but never strands the user: the
    // next Continue / Save draft / finalize re-sends the full body.
    if (state.basics.groupId && hasValidSchedule(state.basics)) {
      const times = basicsToCommandTimes(state.basics)
      const body = {
        huntName: nameOrTeamDefault(state.basics.huntName, teamName),
        startTime: times.startTime,
        endTime: times.endTime,
        leaderIds: effectiveLeaderIds,
        participantIds: effectiveParticipantIds,
        invitationMessage: state.invitationMessage,
        attachmentDocumentIds: state.attachmentDocumentIds,
        attachmentFiles: state.attachmentFiles,
        drives: wizardDrivesToDetail(state.drives),
        huntableGames: state.huntableGames,
        restrictMapToPlannedDrives: state.restrictMapToPlannedDrives,
        // Persist the collected guests on the plan so a reopened draft shows them
        // (delivery still happens at finalize via postHuntGuest once the join code
        // exists). Always sent, so an emptied guest list clears the stored rows.
        guests: state.draftGuests,
        lastStepIndex: Math.min(LAST_STEP_INDEX, state.currentStepIndex + 1),
      }
      try {
        if (!state.draftId) {
          const created = await create.mutateAsync({ groupId: state.basics.groupId, body })
          dispatch({ type: 'set-draft-id', value: created.id })
          void navigate({ to: '/hunts/plan', search: { draftId: created.id }, replace: true })
        } else {
          await update.mutateAsync({ groupId: state.basics.groupId, draftId: state.draftId, body })
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save the hunt draft', err)
      }
    }
    dispatch({ type: 'set-step', value: state.currentStepIndex + 1 })
  }

  async function handleSaveDraft() {
    const times = basicsToCommandTimes(state.basics)
    const body = {
      huntName: nameOrTeamDefault(state.basics.huntName, teamName),
      startTime: times.startTime,
      endTime: times.endTime,
      leaderIds: effectiveLeaderIds,
      participantIds: effectiveParticipantIds,
      invitationMessage: state.invitationMessage,
      attachmentDocumentIds: state.attachmentDocumentIds,
      attachmentFiles: state.attachmentFiles,
      drives: wizardDrivesToDetail(state.drives),
      huntableGames: state.huntableGames,
      restrictMapToPlannedDrives: state.restrictMapToPlannedDrives,
      // Persist the collected guests so a reopened draft shows them; delivery is
      // separate (postHuntGuest at finalize). Always sent, so clearing the list
      // clears the stored rows.
      guests: state.draftGuests,
      lastStepIndex: state.currentStepIndex,
    }
    try {
      if (!state.draftId && state.basics.groupId && hasValidSchedule(state.basics)) {
        const created = await create.mutateAsync({ groupId: state.basics.groupId, body })
        dispatch({ type: 'set-draft-id', value: created.id })
        void navigate({ to: '/hunts/plan', search: { draftId: created.id }, replace: true })
        showToast(t('page.draftSaved'))
      } else if (state.draftId && state.basics.groupId) {
        await update.mutateAsync({ groupId: state.basics.groupId, draftId: state.draftId, body })
        showToast(t('page.draftSaved'))
      }
    } catch (err) {
      // Only surface success above on a resolved save; on failure just log
      // (dedicated failure feedback is a separate story).
      // eslint-disable-next-line no-console
      console.error('Failed to save the hunt draft', err)
    }
  }

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  // True for the whole finalize sequence — including the guest-send loop that
  // runs on the raw `call` after finalize's mutation has already resolved. Folds
  // into `saving` so "Save & send" stays disabled, and guards re-entry, because
  // otherwise a second click during the guest loop would re-run finalize against
  // the already-finalized draft and flash a spurious error.
  const [finalizing, setFinalizing] = useState(false)

  function handleCancel() {
    // Only discarding an already-saved draft needs confirming — with nothing
    // persisted yet there's nothing to lose, so leave immediately. Otherwise
    // open the in-app confirm modal (replaces the native window.confirm).
    if (state.draftId && state.basics.groupId) {
      setShowCancelConfirm(true)
      return
    }
    void navigate({ to: '/hunts' })
  }

  async function confirmCancel() {
    setShowCancelConfirm(false)
    if (state.draftId && state.basics.groupId) {
      try {
        await del.mutateAsync({ groupId: state.basics.groupId, draftId: state.draftId })
      } catch (err) {
        // Leaving is what the user asked for; log the failed cleanup and still go.
        // eslint-disable-next-line no-console
        console.error('Failed to discard the hunt draft', err)
      }
    }
    void navigate({ to: '/hunts' })
  }

  // Validate and stash guests from the participants step. Returns the shape the
  // invite modal wants: `{ ok:false, error }` keeps it open with the message. The
  // addresses are only collected here — nothing is sent until finalize, so no
  // draft id or join code is needed yet (unlike the published-hunt invite action).
  //
  // A comma-separated email paste adds each address at once, matching the
  // published-hunt HuntGuestInviteAction through the same modal; one bad address
  // refuses the whole batch and nothing is added. A phone is always one invite.
  async function handleAddGuest(
    input: { email: string; phone: string; country: string },
  ): Promise<{ ok: boolean; error?: string }> {
    const batch = parseInviteBatch(input)
    if (!batch.ok) return { ok: false, error: t('participants.guest.invalid') }

    // Skip addresses already collected, or repeated within this paste, and add
    // the rest — a duplicate would send the same address twice at finalize. Only
    // when nothing new remains is it reported, so a paste mixing new addresses
    // with a dup still adds the new ones.
    const seen = new Set(state.draftGuests.map((g) => g.value))
    const fresh: MemberInvite[] = []
    for (const inv of batch.invites) {
      if (seen.has(inv.value)) continue
      seen.add(inv.value)
      fresh.push(inv)
    }
    if (fresh.length === 0) return { ok: false, error: t('participants.guest.duplicate') }
    fresh.forEach((inv) => dispatch({ type: 'add-guest', value: inv }))
    return { ok: true }
  }

  async function handleFinalize() {
    // canFinalize gates this button on a team + a valid start, but the draft
    // itself may not exist yet (e.g. the user jumped to the last step via the
    // rail without a Continue ever persisting). Create it on the fly so "Save &
    // send" always works once a team and date are set, instead of silently
    // no-op'ing on a missing draft id.
    if (finalizing) return
    if (!state.basics.groupId) return
    setFinalizing(true)
    const times = basicsToCommandTimes(state.basics)
    const body = {
      huntName: nameOrTeamDefault(state.basics.huntName, teamName),
      startTime: times.startTime,
      endTime: times.endTime,
      leaderIds: effectiveLeaderIds,
      participantIds: effectiveParticipantIds,
      invitationMessage: state.invitationMessage,
      attachmentDocumentIds: state.attachmentDocumentIds,
      drives: wizardDrivesToDetail(state.drives),
      huntableGames: state.huntableGames,
      restrictMapToPlannedDrives: state.restrictMapToPlannedDrives,
      // Persist the collected guests so a reopened draft shows them; delivery is
      // separate (postHuntGuest at finalize). Always sent, so clearing the list
      // clears the stored rows.
      guests: state.draftGuests,
      lastStepIndex: state.currentStepIndex,
    }
    try {
      // Persist the current reducer state before finalizing. The Game step
      // selections happen on the last step with no Continue out, so without
      // this write the finalize endpoint would read a stale draft from
      // Firestore and 422 with "At least one huntable game species is required."
      let draftId = state.draftId
      if (draftId) {
        await update.mutateAsync({ groupId: state.basics.groupId, draftId, body })
      } else {
        const created = await create.mutateAsync({ groupId: state.basics.groupId, body })
        draftId = created.id
        dispatch({ type: 'set-draft-id', value: created.id })
      }
      const published = await finalize.mutateAsync({ groupId: state.basics.groupId, draftId })
      // Now that the hunt exists and finalize has stamped its join code, send the
      // guests collected in the participants step (the email/SMS branch needs that
      // code). A guest send failing must not undo an already-created hunt, so each
      // is independent and failures are counted rather than thrown.
      let guestFailures = 0
      if (published?.id && state.draftGuests.length > 0) {
        const huntState = huntGuestInviteHuntState(basicsToCommandTimes(state.basics).startTime)
        for (const guest of state.draftGuests) {
          try {
            const { status } = await postHuntGuest(call, state.basics.groupId, published.id, {
              ...guest,
              locale: i18n.language,
            })
            trackHuntGuestInvited({ channel: guest.channel, outcome: { status }, source: 'plan_wizard', huntState })
          } catch (e) {
            trackHuntGuestInvited({ channel: guest.channel, outcome: { error: e }, source: 'plan_wizard', huntState })
            guestFailures += 1
            // eslint-disable-next-line no-console
            console.error('Failed to send a hunt guest invite', e)
          }
        }
      }
      // Count the guests actually sent alongside the team-member invites finalize
      // reported, so the success toast reflects everyone invited — invitesSent is
      // computed server-side before these guests go out, so it omits them.
      const guestsSent = state.draftGuests.length - guestFailures
      const totalSent = (published?.invitesSent ?? 0) + guestsSent
      showToast(
        guestFailures > 0
          ? t('page.someGuestInvitesFailed')
          : totalSent > 0
            ? t('page.invitationsSentTo', { count: totalSent })
            : t('page.invitationsSent'),
      )
      void navigate({ to: '/hunts' })
    } catch (err) {
      // On failure, keep the user on the wizard so they can retry — and SHOW
      // why. The server returns the validation reason (e.g. "At least one
      // huntable game species is required."); previously it was only logged, so
      // the send silently did nothing and users had no idea what to fix.
      // eslint-disable-next-line no-console
      console.error('Failed to finalize hunt', err)
      showToast(serverValidationMessage(err) ?? t('page.sendFailed'))
    } finally {
      // Re-enable retry on the error path. On success the component unmounts on
      // navigate, so this is a harmless no-op there.
      setFinalizing(false)
    }
  }

  // What each section actually holds. The rail's check mark reads off this, not
  // off the cursor position — a step the user clicked past and left empty stays
  // a plain numbered step instead of claiming to be complete. Basics is the only
  // required one, so it is also the only one that can show the "!" flag.
  const filled: PlanHuntStepFilled = {
    basics: !!state.basics.groupId && hasValidSchedule(state.basics),
    leaders: state.leaderIds.length > 0,
    participants: state.participantIds.length > 0,
    invitation: planHuntInvitationFilled(
      state.invitationMessage,
      state.attachmentDocumentIds,
      state.attachmentFiles,
    ),
    drives: state.drives.length > 0,
    game: state.huntableGames.length > 0,
  }

  const steps: PlanHuntWizardStep[] = [
    {
      id: 'basics',
      label: t('page.steps.basics'),
      title: t('page.steps.basicsTitle'),
      state: planHuntStepState('basics', 0, state.currentStepIndex, filled),
      // Step 1's required fields are team + a valid start; Continue gates on
      // exactly those and enables the moment both are set. The hunt
      // name is NOT required to advance — it's required to *send* instead (see
      // canFinalize below), so the wizard never blocks forward progress on it.
      // Recomputed every render, so Continue re-locks if a required field clears.
      canContinue: filled.basics,
      content: (
        <BasicsStep
          teams={teams}
          state={state.basics}
          onChange={(v) => {
            // When the team changes, also drop the stale ?draftId from the URL
            // so useDraftHunt stops fetching the old team's draft (the reducer
            // resets state.draftId in lock-step).
            if (v.groupId !== state.basics.groupId && search?.draftId) {
              void navigate({ to: '/hunts/plan', search: {}, replace: true })
            }
            dispatch({ type: 'set-basics', value: v })
          }}
        />
      ),
    },
    {
      id: 'leaders',
      label: t('page.steps.leaders'),
      title: t('page.steps.leadersTitle'),
      state: planHuntStepState('leaders', 1, state.currentStepIndex, filled),
      canContinue: true,
      content: (
        <LeadersStep
          currentUserId={currentUserId}
          members={(members?.members ?? []).filter((m) => !m.deleted).map((m) => ({ id: m.id, name: m.name, role: m.role, avatar: m.imageUrl ?? (m.avatar?.startsWith('http') ? m.avatar : undefined) }))}
          leaderIds={state.leaderIds}
          onChange={(v) => dispatch({ type: 'set-leaders', value: v })}
        />
      ),
    },
    {
      id: 'participants',
      label: t('page.steps.participants'),
      title: t('page.steps.participantsTitle'),
      state: planHuntStepState('participants', 2, state.currentStepIndex, filled),
      canContinue: true,
      content: (
        <ParticipantsStep
          roster={(members?.members ?? []).filter((m) => !m.deleted).map((m) => ({ id: m.id, name: m.name, role: m.role, avatar: m.imageUrl ?? (m.avatar?.startsWith('http') ? m.avatar : undefined) }))}
          leaderIds={state.leaderIds}
          participantIds={state.participantIds}
          onChange={(v) => dispatch({ type: 'set-participants', value: v })}
          guests={state.draftGuests}
          onAddGuest={handleAddGuest}
          onRemoveGuest={(i) => dispatch({ type: 'remove-guest', value: i })}
        />
      ),
    },
    {
      id: 'invitation',
      label: t('page.steps.invitation'),
      title: t('page.steps.invitationTitle'),
      state: planHuntStepState('invitation', 3, state.currentStepIndex, filled),
      canContinue: true,
      content: (
        <InvitationStep
          documents={docList}
          participantCount={state.participantIds.length}
          invitationMessage={state.invitationMessage}
          attachmentDocumentIds={state.attachmentDocumentIds}
          attachmentFiles={state.attachmentFiles}
          onMessageChange={(v) => dispatch({ type: 'set-invitation-message', value: v })}
          onAttachmentsChange={(v) => dispatch({ type: 'set-attachments', value: v })}
          onAttachmentFilesChange={(v) => dispatch({ type: 'set-attachment-files', value: v })}
          // Only enable the image toolbar button + file picker once the draft
          // has been created (first Continue), so uploads have a known target
          // path. Before that, the editor still works but those buttons stay
          // hidden, avoiding a confusing "Save draft first" error.
          onUploadImage={state.draftId ? uploadImage : undefined}
          onUploadAttachment={state.draftId ? uploadAttachment : undefined}
          onResolveAttachmentUrl={resolveAttachmentUrl}
        />
      ),
    },
    {
      id: 'drives',
      label: t('page.steps.drives'),
      title: t('page.steps.drivesTitle'),
      state: planHuntStepState('drives', 4, state.currentStepIndex, filled),
      // A drive with no stands is invalid server-side (standCount must be
      // 1–200). Block Continue until every added drive has a stand — the step
      // shows an inline warning naming the empty drive. Drives are optional, so
      // zero drives is fine.
      canContinue: emptyDriveNames(state.drives).length === 0,
      content: (
        <PlanHuntDrivesStep
          drives={state.drives}
          teamPins={teamPins}
          boundaryAreas={grounds.boundaryAreas}
          forbiddenPolygons={grounds.forbiddenPolygons}
          appearance={huntMapAppearance}
          participants={driveParticipants}
          teamName={teamName}
          subAreas={driveBaseSubAreas}
          pinTypeCatalog={pinTypeCatalog}
          driveSubAreaScopeEnabled={subAreaScopeEnabled}
          mapScopeVisible={subAreaScopeEnabled}
          restrictMapToPlannedDrives={state.restrictMapToPlannedDrives}
          onToggleRestrictMapToPlannedDrives={(v) => dispatch({ type: 'set-restrict-map', value: v })}
          onChange={(v) => dispatch({ type: 'set-drives', value: v })}
        />
      ),
    },
    {
      id: 'game',
      label: t('page.steps.game'),
      title: t('page.steps.gameTitle'),
      state: planHuntStepState('game', 5, state.currentStepIndex, filled),
      canContinue: true,
      content: (
        <GameStep
          catalog={gameCatalog}
          selections={state.huntableGames}
          onChange={(v) => dispatch({ type: 'set-huntable-games', value: v })}
        />
      ),
    },
  ]

  return (
    <>
      <PlanHuntWizardShell
        steps={steps}
        currentStepId={steps[state.currentStepIndex]?.id ?? 'basics'}
        onStepSelect={(i) => dispatch({ type: 'set-step', value: i })}
        onBack={() => dispatch({ type: 'set-step', value: Math.max(0, state.currentStepIndex - 1) })}
        onContinue={handleContinue}
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        onFinalize={handleFinalize}
        saving={create.isPending || update.isPending || del.isPending || finalize.isPending || finalizing}
        // A drive with no stands is rejected server-side on EVERY write — save
        // draft included (standCount must be 1-200) — so Save draft is disabled
        // while any drive is empty, matching Continue/Finalize. The step's inline
        // warning names the offending drive.
        canSaveDraft={emptyDriveNames(state.drives).length === 0}
        // Save & send needs only a team and a valid start. The hunt name and
        // huntable games are OPTIONAL (matching mobile) — a blank name defaults
        // to the team name, and zero games is allowed. Leaders/participants
        // auto-default at submit. The only hard block left is an empty drive
        // (server rejects standCount 0). The shell shows a "add the required
        // details first" tooltip while disabled.
        canFinalize={filled.basics && emptyDriveNames(state.drives).length === 0}
      />
      {showCancelConfirm && (
        <ConfirmModal
          title={t('page.cancelConfirm.title')}
          description={t('page.cancelConfirm.description')}
          confirmLabel={t('page.cancelConfirm.confirm')}
          cancelLabel={t('page.cancelConfirm.cancel')}
          tone="danger"
          onConfirm={confirmCancel}
          onCancel={() => setShowCancelConfirm(false)}
        />
      )}
    </>
  )
}
